import { useEffect, useState } from 'react';
import { Modal, Button, Group, NumberInput, Text, Divider } from '@mantine/core';
import { useWallLengthStore } from '../stores/WallLengthStore';
import { useUnitStore } from '../stores/UnitStore';
import { feetInchesToMeters, formatMeters, metersToFeetInches } from '../editor/editor/units';
import { FloorPlan } from '../editor/editor/objects/FloorPlan';

/**
 * Typing a measured wall length in, rather than dragging until the label looks
 * about right. The far end of the wall slides along its own direction, so the
 * angle you drew is preserved and only the length changes.
 */
export function WallLengthModal() {
    const { wall, close } = useWallLengthStore();
    const { unit } = useUnitStore();

    const [feet, setFeet] = useState<number>(0);
    const [inches, setInches] = useState<number>(0);
    const [meters, setMeters] = useState<number>(0);

    useEffect(() => {
        if (!wall) {
            return;
        }
        const current = wall.getClearSpanMeters();
        const split = metersToFeetInches(current);
        setFeet(split.feet);
        setInches(split.inches);
        setMeters(Math.round(current * 100) / 100);
    }, [wall]);

    const apply = () => {
        if (!wall) {
            return;
        }
        const target = unit === 'imperial' ? feetInchesToMeters(feet, inches) : meters;
        if (wall.setClearSpanMeters(target)) {
            FloorPlan.Instance.redrawWalls();
        }
        close();
    };

    return (
        <Modal opened={wall !== null} onClose={close} title="Wall length" size="sm">
            <Text size="sm" color="dimmed">
                Wall to wall, inside face to inside face. That is the number a tape
                measure gives you across the room.
            </Text>
            <Divider my="sm" />
            {unit === 'imperial' ? (
                <Group grow align="flex-end">
                    <NumberInput
                        label="Feet"
                        value={feet}
                        onChange={(v) => setFeet(v ?? 0)}
                        min={0}
                        max={200}
                        autoFocus
                    />
                    <NumberInput
                        label="Inches"
                        value={inches}
                        onChange={(v) => setInches(v ?? 0)}
                        min={0}
                        max={11}
                    />
                </Group>
            ) : (
                <NumberInput
                    label="Metres"
                    value={meters}
                    onChange={(v) => setMeters(v ?? 0)}
                    precision={2}
                    step={0.05}
                    min={0}
                    autoFocus
                />
            )}
            <Text size="xs" color="dimmed" mt="xs">
                Currently {wall ? formatMeters(wall.getClearSpanMeters()) : ''}. The far
                end of the wall is the end that moves, so a wall joined to that
                corner will shift with it. Set the walls you are surest of first.
            </Text>
            <Group position="right" mt="md">
                <Button variant="default" onClick={close}>Cancel</Button>
                <Button onClick={apply}>Set length</Button>
            </Group>
        </Modal>
    );
}
